import { useState } from "react";
import axios from "axios";
import { Minus, Plus, X } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CartDrawer = () => {
  const { items, subtotal, setQty, removeItem, isOpen, setIsOpen } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);

  const handleCheckout = async () => {
    if (items.length === 0) return;
    setCheckingOut(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        items: items.map((i) => ({ lookup_key: i.product.lookupKey, quantity: i.qty })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Checkout could not be started. Please try again."
      );
      setCheckingOut(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        className="bg-[var(--bg)] border-l border-[var(--line)] w-full sm:max-w-md flex flex-col p-0"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[var(--line)]">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 border border-[var(--line)] overflow-hidden shrink-0 bg-[var(--card-bg)]">
              <img
                src="/label.png"
                alt="Bare Essentials label"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <SheetTitle className="serif-head text-2xl text-[var(--ink)]">Your Selection</SheetTitle>
              <SheetDescription className="label-caps text-[9px] text-[var(--ink)]/60">
                Bare Essentials &bull; Skincare Refined
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div data-testid="cart-empty-state" className="text-center py-16">
              <p className="serif-head text-lg text-[var(--ink)]">Your selection is empty</p>
              <p className="text-xs text-[var(--ink)]/60 mt-2 font-light">
                Choose one of the four scent formulations to begin.
              </p>
              <button
                type="button"
                data-testid="cart-browse-button"
                onClick={() => setIsOpen(false)}
                className="mt-6 border border-[var(--ink)] px-5 py-2 label-caps text-[10px] text-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--bg)] transition-colors"
              >
                Browse Scents
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => (
                <div
                  key={item.slug}
                  data-testid={`cart-item-${item.slug}`}
                  className="border border-[var(--line)] bg-[var(--card-bg)] p-4 flex gap-4"
                >
                  <div className="w-16 h-16 border border-[var(--line)] overflow-hidden shrink-0">
                    <img
                      src={item.product.image}
                      alt={item.product.imageAlt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="label-caps text-[8px] text-[var(--ink)]/60">
                          {item.product.tag}
                        </div>
                        <div className="serif-head text-base text-[var(--ink)] leading-tight">
                          {item.product.name}
                        </div>
                      </div>
                      <button
                        type="button"
                        data-testid={`cart-remove-${item.slug}`}
                        onClick={() => removeItem(item.slug)}
                        className="text-[var(--ink)]/50 hover:text-[var(--ink)] transition-colors"
                        aria-label={`Remove ${item.product.name}`}
                      >
                        <X className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center border border-[var(--line)]">
                        <button
                          type="button"
                          data-testid={`cart-qty-minus-${item.slug}`}
                          onClick={() => setQty(item.slug, item.qty - 1)}
                          className="px-2 py-1 text-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--bg)] transition-colors"
                          aria-label="Decrease quantity"
                        >
                          <Minus className="w-3 h-3" strokeWidth={1.5} />
                        </button>
                        <span
                          data-testid={`cart-qty-${item.slug}`}
                          className="mono-num text-xs px-3 text-[var(--ink)]"
                        >
                          {item.qty}
                        </span>
                        <button
                          type="button"
                          data-testid={`cart-qty-plus-${item.slug}`}
                          onClick={() => setQty(item.slug, item.qty + 1)}
                          className="px-2 py-1 text-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--bg)] transition-colors"
                          aria-label="Increase quantity"
                        >
                          <Plus className="w-3 h-3" strokeWidth={1.5} />
                        </button>
                      </div>
                      <span className="mono-num text-xs font-semibold text-[var(--ink)]">
                        ${item.product.price * item.qty}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[var(--line)] px-6 py-5 bg-[var(--card-bg)]">
            <div className="flex items-center justify-between mb-4">
              <span className="label-caps text-[10px] text-[var(--ink)]/70">Subtotal</span>
              <span data-testid="cart-subtotal" className="mono-num text-sm font-semibold text-[var(--ink)]">
                ${subtotal}.00 USD
              </span>
            </div>
            <button
              type="button"
              data-testid="checkout-button"
              onClick={handleCheckout}
              disabled={checkingOut}
              className="w-full border border-[var(--ink)] bg-[var(--ink)] text-[var(--bg)] px-8 py-3.5 label-caps text-[11px] hover:bg-transparent hover:text-[var(--ink)] transition-colors disabled:opacity-50"
            >
              {checkingOut ? "Preparing Checkout…" : "Checkout with Stripe"}
            </button>
            <p className="mt-3 text-center text-[10px] text-[var(--ink)]/50 label-caps">
              Tracked dispatch from Canada
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
